import { Button, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { DateTime as DT } from 'luxon';
import type { ProcessRunListItemType } from '@econtractor/types';
import SolarIcon from 'components/icons/SolarIcon';
import ProcessRunProgress from '../ProcessRunProgress';
import RowTitle from '../../shared/RowTitle';

interface ProcessRunsTableRowProps {
    process: ProcessRunListItemType;
    onEdit: (process: ProcessRunListItemType) => void;
    onLaunch?: (process: ProcessRunListItemType) => void;
    onDelete?: (process: ProcessRunListItemType) => void;
    launching?: boolean;
}

const ProcessRunsTableRow = ({ process, onEdit, onLaunch, onDelete, launching }: ProcessRunsTableRowProps) => {
    const { t } = useTranslation(['Tasks', 'common']);
    const created = DT.fromMillis(process.dt || 0, { zone: 'local' });
    const hasCreated = created.isValid && process.dt > 0;
    const rowTone = process.status === 'draft'
        ? 'bg-light-warning'
        : process.status === 'active'
            ? 'bg-light-primary'
            : 'bg-light-success';

    return (
        <tr className={`cursor-pointer ${rowTone}`} onClick={() => onEdit(process)}>
            <td className="name">
                <RowTitle name={process.name} description={process.description} />
            </td>
            <td className="created">
                {hasCreated ? (
                    <>
                        <span className="d-block">{created.toFormat('dd.MM.yyyy')}</span>
                        <span className="text-muted">{created.toFormat('HH:mm')}</span>
                    </>
                ) : (
                    <span className="text-muted">—</span>
                )}
            </td>
            <td className="category text-break">
                {process.categoryName ? process.categoryName : <span className="text-muted">—</span>}
            </td>
            <td className="status">
                <ProcessRunProgress status={process.status} done={process.progressDone} total={process.progressTotal} />
            </td>
            <td className="tasks">
                <span className="process-run-tasks">
                    <span className="badge bg-light-secondary fs-12 process-run-tasks__chip" title={t('processRuns.tasksAllHint')}>
                        <span className="process-run-tasks__num">{process.taskCount}</span>
                        {t('processRuns.tasksAll')}
                    </span>
                    <span
                        className={`badge fs-12 process-run-tasks__chip ${
                            process.progressTotal < process.taskCount ? 'bg-light-primary' : 'bg-light-secondary'
                        }`}
                        title={t('processRuns.tasksLiveHint')}
                    >
                        <span className="process-run-tasks__num">{process.progressTotal}</span>
                        {t('processRuns.tasksLive')}
                    </span>
                </span>
            </td>
            <td className="action text-end" onClick={(event) => event.stopPropagation()}>
                {process.status === 'draft' ? (
                    <span className="d-inline-flex align-items-center gap-1">
                        {onLaunch ? (
                            <Button
                                type="button"
                                size="sm"
                                className="p-1 py-0"
                                variant="light-success"
                                disabled={launching}
                                onClick={() => onLaunch(process)}
                                aria-label={t('processRuns.btnLaunch')}
                                aria-busy={launching}
                                title={t('processRuns.btnLaunch')}
                            >
                                {launching ? (
                                    <Spinner animation="border" size="sm" role="status" />
                                ) : (
                                    <SolarIcon name="play-fill" className="fs-14" />
                                )}
                            </Button>
                        ) : null}
                        {onDelete ? (
                            <Button
                                type="button"
                                size="sm"
                                className="p-1 py-0"
                                variant="light-danger"
                                onClick={() => onDelete(process)}
                                aria-label={t('common:buttons.delete')}
                            >
                                <SolarIcon name="trash3" />
                            </Button>
                        ) : null}
                    </span>
                ) : null}
            </td>
        </tr>
    );
};

export default ProcessRunsTableRow;
